import LoadingButton from '@mui/lab/LoadingButton';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import useTheme from '@mui/material/styles/useTheme';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { DataItem } from '~components/DataItem';
import ControlledNumberField from '~components/Form/ControlledNumberField';
import SectionCard from '~components/SectionCard';
import { Campaign, SmsSettings } from '~pages/CampaignManagement/domain';
import { deepCompare } from '~utils/Functions';

interface SMSSettingsFormProps {
  campaign: Campaign;
  isEdit: boolean;
  submitting: boolean;
  toggleEdit?: () => void;
  onSubmit: (data: SmsSettings) => Promise<void>;
  onRemove: () => Promise<void>;
}

interface Form {
  enabled: boolean;
  smsProvider: string;
  replyEndpoint: string;
  fromEndpoint: string;
  replyGraceTimeSeconds: number;
  midJourneyAttempts: number;
  initialTemplate: string;
  midJourneyTemplate: string;
  finalJourneyTemplate: string;
}

const SMSProviders = {
  Twilio: 'twilio',
  Tim: 'tim',
  Ccom: 'ccom',
  Pega: 'pega',
} as const;
const SMSProviderMenuList = Object.entries(SMSProviders).map(([key, value]) => (
  <MenuItem key={value} value={value}>
    {key}
  </MenuItem>
));

const SMSProviderNameByValue = Object.entries(SMSProviders).reduce((state, [key, value]) => {
  return {
    ...state,
    [value]: key,
  };
}, {} as Record<string, string>);

const SMSSettingsForm = ({ campaign, isEdit, submitting, toggleEdit, onSubmit, onRemove }: SMSSettingsFormProps) => {
  const theme = useTheme();
  const isExtraSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    formState: { errors },
    handleSubmit,
    reset,
    control,
    watch,
  } = useForm<Form>({
    defaultValues: {
      enabled: false,
      smsProvider: SMSProviders.Twilio,
      replyEndpoint: '',
      fromEndpoint: '',
      replyGraceTimeSeconds: 0,
      midJourneyAttempts: 0,
      initialTemplate: '',
      midJourneyTemplate: '',
      finalJourneyTemplate: '',
    },
    mode: 'all',
    reValidateMode: 'onChange',
    shouldUnregister: false,
  });
  const disablePerformAction =
    campaign &&
    deepCompare(campaign.smsSettings?.smsProvider ?? SMSProviders.Twilio, watch('smsProvider')) &&
    deepCompare(campaign.smsSettings?.replyEndpoint || '', watch('replyEndpoint')) &&
    deepCompare(campaign.smsSettings?.fromEndpoint || '', watch('fromEndpoint')) &&
    deepCompare(campaign.smsSettings?.replyGraceTimeSeconds || 0, watch('replyGraceTimeSeconds')) &&
    deepCompare(campaign.smsSettings?.midJourneyAttempts || 0, watch('midJourneyAttempts')) &&
    deepCompare(campaign.smsSettings?.initialTemplate || '', watch('initialTemplate')) &&
    deepCompare(campaign.smsSettings?.midJourneyTemplate || '', watch('midJourneyTemplate')) &&
    deepCompare(campaign.smsSettings?.finalJourneyTemplate || '', watch('finalJourneyTemplate'));
  const smsSettingsEnabled = campaign.smsSettings?.enabled || false;
  const [smsProvider] = watch(['smsProvider']);

  // Handles reset and editable display
  useEffect(() => {
    if (isEdit) {
      reset({
        enabled: campaign.smsSettings?.enabled ?? false,
        smsProvider: campaign.smsSettings?.smsProvider ?? SMSProviders.Twilio,
        replyEndpoint: campaign.smsSettings?.replyEndpoint || '',
        fromEndpoint: campaign.smsSettings?.fromEndpoint || '',
        replyGraceTimeSeconds: campaign.smsSettings?.replyGraceTimeSeconds || 0,
        midJourneyAttempts: campaign.smsSettings?.midJourneyAttempts || 0,
        initialTemplate: campaign.smsSettings?.initialTemplate || '',
        midJourneyTemplate: campaign.smsSettings?.midJourneyTemplate || '',
        finalJourneyTemplate: campaign.smsSettings?.finalJourneyTemplate || '',
      });
    }
  }, [isEdit]);

  const isRequiredField = smsProvider === SMSProviders.Twilio;

  const onSubmitFn = handleSubmit((formData: any) => {
    onSubmit({
      enabled: true,
      smsProvider: formData.smsProvider,
      replyEndpoint: formData.smsProvider === SMSProviders.Twilio ? formData.replyEndpoint || '' : '',
      fromEndpoint: formData.smsProvider === SMSProviders.Twilio ? formData.fromEndpoint || '' : '',
      replyGraceTimeSeconds: formData.replyGraceTimeSeconds,
      midJourneyAttempts: formData.midJourneyAttempts,
      initialTemplate: formData.initialTemplate,
      midJourneyTemplate: formData.midJourneyTemplate,
      finalJourneyTemplate: formData.finalJourneyTemplate,
    });
  });

  const onRemoveFn = () => {
    reset();
    onRemove();
  };

  const onCloseSMSSettingsFn = () => {
    if (!toggleEdit) {
      console.error('onCloseSMSSettingsFn: toggleEdit not defined.');
      return;
    }

    // We only want to run this field change IF sms settings havent existed yet
    // (or enabled was false, with defaults coming through)
    if (campaign.smsSettings === null || !campaign.smsSettings?.enabled) {
      reset();
    }
    toggleEdit();
  };

  return (
    <SectionCard title='Precall SMS Settings' onEdit={toggleEdit}>
      {!smsSettingsEnabled && !isEdit && (
        <Typography variant='body1' component='p'>
          Precall SMS is not configured for this campaign.
        </Typography>
      )}

      {smsSettingsEnabled && !isEdit && (
        <>
          <DataItem
            stacked={isExtraSmall}
            disableMargin
            title='SMS provider'
            tooltip="The provider used to send out precall SMS's to leads. Twilio is currently the only supported SMS Provider."
            value={
              campaign.smsSettings?.smsProvider
                ? SMSProviderNameByValue[campaign.smsSettings?.smsProvider] || campaign.smsSettings.smsProvider
                : undefined
            }
          />

          {campaign.smsSettings?.smsProvider === SMSProviders.Twilio && (
            <>
              <DataItem
                stacked={isExtraSmall}
                title='Reply endpoint'
                tooltip='This is the endpoint that is used when a lead responds to the precall SMS.'
                value={campaign.smsSettings?.replyEndpoint}
              />
              <DataItem
                stacked={isExtraSmall}
                title='From endpoint'
                tooltip='This is what the lead will see as the sender of the SMS.'
                value={campaign.smsSettings?.fromEndpoint}
              />
            </>
          )}

          <DataItem
            stacked={isExtraSmall}
            title='Reply grace time seconds'
            tooltip='The amount of time between sending the precall SMS and adding them to the dial queue.'
            value={campaign.smsSettings?.replyGraceTimeSeconds}
          />
          <DataItem
            stacked={isExtraSmall}
            title='Mid journey attempts'
            tooltip='The number of call attempts that will be made before sending the mid-journey SMS.'
            value={campaign.smsSettings?.midJourneyAttempts}
          />
          <DataItem
            stacked={isExtraSmall}
            title='Inital template'
            tooltip='The template of the initial SMS sent to a lead.'
            value={campaign.smsSettings?.initialTemplate}
          />
          <DataItem
            stacked={isExtraSmall}
            title='Mid journey template'
            tooltip='The template of the mid-journey SMS sent to a lead.'
            value={campaign.smsSettings?.midJourneyTemplate}
          />
          <DataItem
            stacked={isExtraSmall}
            title='Final journey template'
            tooltip='The template of the final journey SMS sent to a lead.'
            value={campaign.smsSettings?.finalJourneyTemplate || '-'}
          />
        </>
      )}

      {isEdit && (
        <form onSubmit={onSubmitFn} noValidate>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Controller
                name='smsProvider'
                control={control}
                rules={{ required: 'SMS provider is required.' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    variant='outlined'
                    id='smsProvider'
                    label='SMS provider'
                    disabled={submitting}
                    required={true}
                    error={Boolean(errors.smsProvider)}
                    helperText={errors.smsProvider?.message}>
                    {SMSProviderMenuList}
                  </TextField>
                )}
              />
            </Grid>

            {isRequiredField && (
              <Grid item xs={12}>
                <Controller
                  name='replyEndpoint'
                  control={control}
                  rules={{ required: 'reply endpoint is required.' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      variant='outlined'
                      id='replyEndpoint'
                      label='Reply endpoint'
                      disabled={submitting}
                      required={true}
                      error={Boolean(errors.replyEndpoint)}
                      helperText={errors.replyEndpoint?.message}
                    />
                  )}
                />
              </Grid>
            )}

            {isRequiredField && (
              <Grid item xs={12}>
                <Controller
                  name='fromEndpoint'
                  control={control}
                  rules={{ required: 'from endpoint is required.' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      variant='outlined'
                      id='fromEndpoint'
                      label='From endpoint'
                      disabled={submitting}
                      required={true}
                      error={Boolean(errors.fromEndpoint)}
                      helperText={errors.fromEndpoint?.message}
                    />
                  )}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <ControlledNumberField
                name='replyGraceTimeSeconds'
                control={control}
                rules={{ required: 'reply grace time seconds is required.' }}
                label='Reply grace time seconds'
                disabled={submitting}
                required={true}
                error={Boolean(errors.replyGraceTimeSeconds)}
                helperText={errors.replyGraceTimeSeconds?.message}
              />
            </Grid>

            <Grid item xs={12}>
              <ControlledNumberField
                name='midJourneyAttempts'
                control={control}
                rules={{ required: 'mid journey attempts is required.' }}
                label='Mid journey attempts'
                disabled={submitting}
                required={true}
                error={Boolean(errors.midJourneyAttempts)}
                helperText={errors.midJourneyAttempts?.message}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name='initialTemplate'
                control={control}
                rules={{ required: 'initial template is required.' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    multiline
                    rows={4}
                    variant='outlined'
                    id='initialTemplate'
                    label='Initial template'
                    disabled={submitting}
                    required={true}
                    error={Boolean(errors.initialTemplate)}
                    helperText={errors.initialTemplate?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name='midJourneyTemplate'
                control={control}
                rules={{ required: 'mid journey template is required.' }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    multiline
                    rows={4}
                    variant='outlined'
                    id='midJourneyTemplate'
                    label='Mid journey template'
                    disabled={submitting}
                    required={true}
                    error={Boolean(errors.midJourneyTemplate)}
                    helperText={errors.midJourneyTemplate?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name='finalJourneyTemplate'
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    multiline
                    rows={4}
                    variant='outlined'
                    id='finalJourneyTemplate'
                    label='Final journey template'
                    disabled={submitting}
                    error={Boolean(errors.finalJourneyTemplate)}
                    helperText={errors.finalJourneyTemplate?.message}
                  />
                )}
              />
            </Grid>

            <Grid sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }} item xs={12}>
              {campaign.smsSettings?.enabled && (
                <div>
                  <LoadingButton
                    variant='contained'
                    disableElevation
                    loading={submitting}
                    onClick={onRemoveFn}
                    color='secondary'>
                    Remove
                  </LoadingButton>
                </div>
              )}

              {/* Used to maintain flex space-between when the remove button isnt present */}
              {!campaign.smsSettings?.enabled && <div></div>}

              <div>
                <Button onClick={onCloseSMSSettingsFn}>Cancel</Button>

                <LoadingButton
                  sx={{ marginLeft: 1 }}
                  type='submit'
                  variant='contained'
                  disableElevation
                  disabled={disablePerformAction}
                  loading={submitting}
                  color='primary'>
                  {campaign.smsSettings === null ? 'Add' : 'Update'}
                </LoadingButton>
              </div>
            </Grid>
          </Grid>
        </form>
      )}
    </SectionCard>
  );
};

export default SMSSettingsForm;
